import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { consume } from '@lit/context';
import { nsStripeServiceContext } from '../../../../ns-services/contexts.js';

import { ButtressEntity } from '@buttress/crag';

import { NSStripeService } from '../../../../ns-services/ns-stripe-service.js';

import NSJobComponent from '../../job.js';
import JobSchema from '../../../../models/job-schema.js';
import Invoice from '../../../../models/invoice.js';
import { callLambda } from '../../../../helpers/lambda.js';
import '../../../../ns-elements/ns-job-layout.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import definition from '@job-schemas/subscription/add-card.json';
import { jobSchema } from '../../job-schema.js';

const enum CardStatus {
  PENDING = 0,
  CAN_COMPLETE = 1,
}

@customElement(NSAddSubscriptionCard.is)
export default class NSAddSubscriptionCard extends NSJobComponent {
  static get is() {
    return 'ns-add-subscription-card';
  }

  @consume({ context: nsStripeServiceContext })
  private __stripe?: NSStripeService;

  private __subscription?: ButtressEntity;

  @state()
  private __jobMessage?: string;

  @state()
  private __cardStatus?: CardStatus = CardStatus.PENDING;

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { complete: { type: 'hidden' } } });
  }

  async connectedCallback() {
    await super.connectedCallback();

    if (!this._db) throw new Error('Unable to find db service');

    if (!this._directory) throw new Error('Unable to find dir service');

    if (!this._auth) throw new Error('Unable to find auth service');

    if (!this.__stripe) throw new Error('Unable to find stripe service');

    if (!this._job) throw new Error('No job available');

    this.__subscription = await this.loadInput('subscription', 'subscriptionId');

    const queryCall = await this._db.query('invoice', {
      status: {
        $eq: 'PAID',
      },
      subscriptionId: {
        $eq: this.__subscription.id,
      },
    });
    let invoices = queryCall.results as unknown as Invoice[];
    invoices = invoices.sort((a, b) => new Date(a.paidOn).getTime() - new Date(b.paidOn).getTime());
    const latestInvoice = invoices.slice().pop();

    this.__canComplete();

    this._subscriptions.subscribe(`job.${this._job.id}.metadata.*`, () => this.__canComplete());

    await this.__stripe.mountStripeElement(this, null, latestInvoice?.billing?.address);
  }

  disconnectedCallback() {
    this.__stripe?.cleanElements();

    super.disconnectedCallback();
  }

  private async __canComplete() {
    if (!this._db) throw new Error('Missing required db service');
    if (!this._job || !this._job.id) throw new Error('No job available');

    const job = this._db.get(`job.${this._job.id}`);
    if (!job) throw new Error(`Could not fetch job with ID: ${this._job.id} from db store`);

    const messageMetadata = job.metadata.find((m: { key: string; value: string }) => m.key === 'MESSAGE');
    if (messageMetadata && messageMetadata.value) {
      this.__jobMessage = messageMetadata.value;
      this.__cardStatus = CardStatus.CAN_COMPLETE;
    }
  }

  private async __addCard() {
    this.clearError();
    if (!this._directory || !this._directory.me) throw new Error('Unable to find directory service');
    if (!this._auth) throw new Error('Unable to find auth service');
    if (!this.__stripe) throw new Error('Unable to find stripe service');
    if (!this._job) throw new Error('No job available');
    if (!this.__subscription) {
      this.showError('Unable to find a subscription to be updated');
      throw new Error('Can not find subscription to be updated');
    }

    this.loading('Updating Subscription Card Details');
    const { paymentMethod, error } = await this.__stripe.addPaymentMethodCard();
    if (error) {
      this.loaded();
      this.showError(error.message);
      throw new Error(error.message);
    }
    if (!paymentMethod) {
      this.loaded();
      this.showError('Something went wrong whilst creating payment method');
      throw new Error('Something went wrong whilst creating payment method');
    }

    try {
      await callLambda(
        'subscription/add-card',
        'POST',
        this._auth.token,
        JSON.stringify({
          paymentMethod: paymentMethod,
          subscriptionId: this.__subscription.id,
          jobId: this._job.id,
        }),
      );
    } catch (err: any) {
      this.loaded();
      this.showError(err, 'Something went wrong');
      throw new Error(err.message);
    }

    this.loaded();
  }

  render() {
    if (this.__cardStatus === CardStatus.CAN_COMPLETE) {
      return html`
        <ns-job-layout>
          <div class="job-panel">
            <p>${this.__jobMessage}</p>
          </div>
        </ns-job-layout>
      `;
    }

    return html`
      <ns-job-layout>
        <p slot="intro">Please fill the below card details to change your payment card.</p>
        <div class="job-panel">
          <slot name="stripe-payment-form"></slot>
        </div>
        <div slot="aside" class="primary-info">
          <span class="info-icon" aria-hidden="true">i</span>
          <p>The next payment will come out of this new card.</p>
        </div>
      </ns-job-layout>
    `;
  }

  renderActions() {
    if (this.__cardStatus === CardStatus.CAN_COMPLETE) {
      return html`
        <wa-button variant="brand" type="button" @click=${() => this.outcome('complete')}>Complete</wa-button>
      `;
    }
    return html`
      <wa-button variant="brand" type="button" @click=${() => this.__addCard()}>Add Card</wa-button>
    `;
  }
}
